import { useEffect, useState } from 'react';
import { useReloadOnChange } from '../lib/changes';
import { ApiError, api, quarterLabel, type RequestSummary } from '../lib/api';
import { addDays, dayLabel } from '../lib/dates';
import { navigate } from '../lib/router';
import { CoverageForecast } from './CoverageForecast';

/**
 * The scheduler's side of requests (D29): open a quarter with a closing
 * date, watch who has answered, read what they asked for, and see how many
 * physicians want or cannot work each week before filling the grid.
 */
export function AdminRequests({ quarter }: { quarter: string }) {
  const [data, setData] = useState<RequestSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [closesOn, setClosesOn] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const load = () => {
    setError(null);
    api
      .requestSummary(quarter)
      .then((d) => {
        setData(d);
        setClosesOn(d.window?.closesOn ?? '');
        setMessage(d.window?.message ?? '');
      })
      .catch((e: Error) => setError(e.message));
  };
  useEffect(() => {
    let ignore = false;
    setData(null);
    setError(null);
    api
      .requestSummary(quarter)
      .then((d) => {
        if (ignore) return;
        setData(d);
        setClosesOn(d.window?.closesOn ?? '');
        setMessage(d.window?.message ?? '');
      })
      .catch((e: Error) => { if (!ignore) setError(e.message); });
    return () => { ignore = true; };
  }, [quarter]);
  useReloadOnChange(load);

  const saveWindow = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.setRequestWindow(quarter, { closesOn, message: message.trim() || null });
      load();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="error">Couldn't load requests ({error}).</p>;
  if (!data) return <p className="muted">Loading {quarterLabel(quarter)}…</p>;

  const rows = onlyMissing ? data.physicians.filter((p) => !p.request) : data.physicians;
  const step = (n: number) => {
    const [y, q] = quarter.split('-Q').map(Number);
    const idx = y * 4 + (q - 1) + n;
    navigate(`/admin/requests?quarter=${Math.floor(idx / 4)}-Q${(idx % 4) + 1}`);
  };

  return (
    <section className="reqadmin">
      <div className="gridtools">
        <div className="quarterpicker">
          <button type="button" onClick={() => step(-1)} aria-label="Previous quarter">‹</button>
          <h1>{quarterLabel(quarter)} requests</h1>
          <button type="button" onClick={() => step(1)} aria-label="Next quarter">›</button>
        </div>
        <span className="muted">
          {dayLabel(data.weeks[0]).replace(/^\w+, /, '')} – {dayLabel(addDays(data.weeks[data.weeks.length - 1], 6)).replace(/^\w+, /, '')} · {data.responded} of {data.physicians.length} responded
        </span>
        <button className="linklike" type="button" onClick={() => navigate(`/admin/grid?quarter=${quarter}`)}>open the grid</button>
      </div>

      <div className="card windowcard">
        <h2>Request window</h2>
        {data.window ? (
          <p className={data.window.open ? 'ok' : 'warn'}>
            {data.window.open ? `Open — physicians can submit and change requests through ${dayLabel(data.window.closesOn)}.` : `Closed ${dayLabel(data.window.closesOn)} — the form is read-only for physicians.`}
          </p>
        ) : (
          <p className="muted">Not open yet — physicians cannot see this quarter until you set a closing date.</p>
        )}
        <div className="windowform">
          <label className="field">
            <span>Physicians can answer through</span>
            <input type="date" value={closesOn} onChange={(e) => setClosesOn(e.target.value)} />
          </label>
          <label className="field grow">
            <span>Note shown at the top of the form (optional)</span>
            <input type="text" value={message} maxLength={1000} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Q2 runs Mar 30 – Jun 28. Mark the weeks you want and the weeks you cannot work." />
          </label>
          <button type="button" className="primary" disabled={busy || !closesOn} onClick={saveWindow}>{data.window ? 'Update' : 'Open requests'}</button>
        </div>
        {problem && <p className="error">{problem}</p>}
      </div>

      <CoverageForecast quarter={quarter} />

      <div className="gridscroll">
        <table className="grid requests">
          <thead>
            <tr>
              <th className="sticky name">
                Physician
                <label className="small muted filter"><input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} /> only those who haven't answered</label>
              </th>
              <th>Answered</th>
              <th>Target</th>
              {data.weeks.map((w, i) => (
                <th key={w} className="week" title={`Week of ${w}`}>
                  <div className="wk-days">{dayLabel(w).replace(/^\w+, /, '')}</div>
                  <div className="wk-month">wk {i + 1}</div>
                </th>
              ))}
              <th className="notes">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.abbreviation} className={p.request ? '' : 'missing'}>
                <th className="sticky name" title={p.abbreviation}>
                  {[p.lastName, p.firstName].filter(Boolean).join(', ')}
                  {p.status !== 'Active' && <span className="status">{p.status}</span>}
                </th>
                <td className="small">{p.request ? new Date(p.request.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : <span className="muted">—</span>}</td>
                <td className="small">{p.request?.targetWeeks ?? ''}</td>
                {data.weeks.map((w) => {
                  // Two answers and a hole (D58): green wants it, red can't, and a blank cell
                  // means nobody has heard — including on a request that was submitted, since
                  // submitting is no longer itself an answer for the weeks left unmarked.
                  const offer = (p.partials ?? []).find((x) => x.week === w);
                  const kind = p.request?.wantedWeeks.includes(w) ? 'want' : p.request?.blackoutWeeks.includes(w) ? 'blackout' : offer ? 'partial' : 'unanswered';
                  const title =
                    kind === 'want'
                      ? 'wants this week'
                      : kind === 'blackout'
                        ? 'cannot work this week'
                        : offer
                          ? `offered ${offer.dates.length} day${offer.dates.length === 1 ? '' : 's'}: ${offer.dates.join(', ')}${offer.note ? ` — ${offer.note}` : ''}`
                          : 'no answer yet — they cannot be scheduled into it';
                  return (
                    <td key={w} className={`cell mark ${kind}`} title={title}>
                      {kind === 'want' ? 'W' : kind === 'blackout' ? '✕' : kind === 'partial' ? 'P' : ''}
                    </td>
                  );
                })}
                <td className="notes">{p.request?.note ?? ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="foot-head">
              <th className="sticky name">Want / can't</th>
              <th />
              <th />
              {data.tally.map((t) => (
                <td key={t.week} className="cell small">
                  <span className="tally want">{t.want}</span> / <span className="tally blackout">{t.blackout}</span>
                  {t.partial > 0 && <> / <span className="tally partial">{t.partial}</span></>}
                </td>
              ))}
              <td className="notes muted">want / can't / part-week · green wants it, red can't, P is an offer to cover some days, blank means no answer yet — only a week somebody wants is scheduled automatically, a P is yours to take up</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
